/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */
import { popper } from '@popperjs/core';
import classNames from 'classnames';
import React, { ReactElement } from 'react';
import { useAutoControlled } from '../..';
import { Button, Icon } from '../../atoms';
import {
    MenuItem,
    MenuItemGroup,
    MenuItemLabel,
    MenuItemLink,
    MenuItemProps,
} from '../../atoms/menuItem/menuItem';
import { Popover, PopoverProps } from '../../molecules/popover/popover';
import { ReactChildren } from '../../types';
import * as utils from '../../utils';

/**
 * @name      o-context-menu
 * @type      organism
 * @author    tss1imb
 * @copyright Robert Bosch GmbH
 *
 * @description
 * representation of Context Menu
 */

export interface ContextMenuProps
    extends React.HTMLAttributes<HTMLElement>,
        React.RefAttributes<HTMLDivElement> {
    /**
     * Flag to indicate the open state of the context menu. Note: If you provide this property
     * you must also provide the 'onOpenChange' event handler in order to have the context menu
     * uncontrolled.
     */
    open?: boolean;
    /**
     * Default open state.
     */
    defaultOpen?: boolean;
    /**
     * Open state change event handler. Note: If you provide this handler you need also to provide the 'open'
     * property.
     */
    onOpenChange?: (open: boolean) => void;
    /**
     * The close key for closing the context menu (defaults to Escape).
     */
    closeKey?: string;
    /**
     * The popover icon for closing the menu.
     */
    closePopoverIcon?: string;
    /**
     * The popover icon for opening the menu.
     */
    openPopoverIcon?: string;
    /**
     * The open button label.
     */
    ariaOpenPopoverButtonLabel?: string;
    /**
     * The close button label.
     */
    ariaClosePopoverButtonLabel?: string;

    /**
     * The ARIA label for menubar.
     */
    ariaMenuLabel?: string;
    /**
     * The underlying popover properties to passthrough.
     */
    popover?: PopoverProps;

    /**
     * The children of the context menu.
     */
    children?: ReactChildren;
    /**
     * The context menu trigger e.g. a <Button />.
     */
    trigger?: ReactElement;
}

export interface ContextSubMenuProps
    extends React.LiHTMLAttributes<HTMLLIElement> {
    label?: string;
    ariaOpenButtonLabel?: string;
    children?:
        | Array<React.ReactElement<MenuItemProps>>
        | React.ReactElement<MenuItemProps>;
}

export const ContextSubMenu: React.FC<ContextSubMenuProps> = ({
    label,
    ariaOpenButtonLabel,
    children,
    ...rest
}) => {
    return (
        <li className="a-menu-item" {...rest}>
            <button type="button" className="a-menu-item__group">
                <MenuItemLabel>{label}</MenuItemLabel>
                <Icon
                    iconName="forward-right"
                    title={ariaOpenButtonLabel || 'Open'}
                />
            </button>

            <ul className={`o-context-menu__menuSubitems--beside`} role="menu">
                {children}
            </ul>
        </li>
    );
};

interface ContextMenuApi {
    Group?: typeof MenuItemGroup;
    Item?: typeof MenuItem;
    Link?: typeof MenuItemLink;
    Label?: typeof MenuItemLabel;
    SubMenu?: typeof ContextSubMenu;
}

const ContextMenuComponent = React.forwardRef<HTMLElement, ContextMenuProps>(
    (props, forwardedRef) => {
        const {
            onOpenChange,
            open,
            defaultOpen,
            closePopoverIcon,
            closeKey,
            openPopoverIcon,
            popover = {},
            ariaOpenPopoverButtonLabel = 'Open context menu',
            ariaClosePopoverButtonLabel = 'Close context menu',
            ariaMenuLabel = 'Context menu',
            children,
            trigger,
            ...rest
        } = props;

        const [contextMenuOpen, setContextMenuOpen] = useAutoControlled({
            defaultValue: defaultOpen,
            value: open,
            initialValue: false,
        });

        function toggleOpen(state: boolean) {
            if (typeof onOpenChange === 'function') {
                onOpenChange(state);
            }
            setContextMenuOpen(state);
        }

        const defaultTrigger = () => {
            return (
                !trigger && (
                    <div>
                        <Button
                            className="o-context-menu__trigger"
                            icon={openPopoverIcon || 'options'}
                            mode="integrated"
                            aria-haspopup={'false'}
                            data-frok-action="open"
                            title={ariaOpenPopoverButtonLabel}
                            onClick={() => toggleOpen(true)}
                        />
                        <Button
                            className="o-context-menu__trigger"
                            icon={closePopoverIcon || 'close'}
                            mode="integrated"
                            data-frok-action="close"
                            title={ariaClosePopoverButtonLabel}
                            aria-haspopup={'true'}
                            onClick={() => toggleOpen(false)}
                        />
                    </div>
                )
            );
        };

        const handleCloseButtonClick = (ev: React.MouseEvent<HTMLElement>) => {
            toggleOpen(false);
            utils.invoke(popover, 'onCloseButtonClick', ev);
        };

        const handleCloseKeyPressed = (ev: KeyboardEvent) => {
            toggleOpen(false);
            utils.invoke(popover, 'onCloseKeyPressed', ev);
        };

        const handleOutsideClick = (ev: MouseEvent) => {
            toggleOpen(false);
            utils.invoke(popover, 'onOutsideClick', ev);
        };

        const handleOnTriggerClick = (ev: React.MouseEvent<HTMLElement>) => {
            toggleOpen(!contextMenuOpen);
            utils.invoke(popover, 'onTriggerClick', ev);
        };

        const handleOnMenubarClick = (
            ev: React.MouseEvent<HTMLUListElement, MouseEvent>,
        ) => {
            const linkTarget = findLinkTarget(ev);
            if (linkTarget !== null) {
                toggleOpen(false);
                utils.invoke(props, 'onClick', ev);
            }
        };

        const classes = classNames('o-context-menu', {
            '-open': contextMenuOpen,
        });

        return (
            <nav className={classes} ref={forwardedRef} {...rest}>
                <Popover
                    open={contextMenuOpen}
                    closeKey={closeKey}
                    isPopoverArrowMissing={!!trigger}
                    trigger={trigger ?? defaultTrigger()}
                    onCloseButtonClick={handleCloseButtonClick}
                    onCloseKeyPressed={handleCloseKeyPressed}
                    onOutsideClick={handleOutsideClick}
                    onTriggerClick={handleOnTriggerClick}
                    {...popover}
                >
                    <ul
                        className="o-context-menu__menuItems"
                        role="menubar"
                        aria-label={ariaMenuLabel}
                        onClick={handleOnMenubarClick}
                    >
                        {children}
                    </ul>
                </Popover>
            </nav>
        );
    },
);

function findLinkTarget(ev: React.MouseEvent<HTMLUListElement, MouseEvent>) {
    let linkEl = ev.target as HTMLElement;
    while (linkEl !== null) {
        if (linkEl.classList.contains('a-menu-item__link')) {
            return linkEl;
        }
        linkEl = linkEl.parentElement;
    }
    return null;
}

ContextMenuComponent.displayName = 'ContextMenuComponent';

type ContextMenuType = typeof ContextMenuComponent & ContextMenuApi;
export const ContextMenu: ContextMenuType = ContextMenuComponent;
ContextMenu.Group = MenuItemGroup;
ContextMenu.Item = MenuItem;
ContextMenu.Link = MenuItemLink;
ContextMenu.Label = MenuItemLabel;
ContextMenu.SubMenu = ContextSubMenu;
